// ============================================================================
// Task List URL Slug Helpers
// ============================================================================
// A custom task list can carry an optional, human-friendly "slug" alias so it
// is reachable at #/<slug> (e.g. #/afs) instead of the opaque
// #/list-view?id=<uuid>. These pure helpers normalize, validate, and build URLs
// around that slug. The backing `slug` column + a case-insensitive partial
// unique index live on public.task_lists.
// ============================================================================

import { router } from 'app/router/router.js';

/**
 * Normalize free-form input into a URL-safe slug: lowercased, spaces and
 * underscores become dashes, anything outside [a-z0-9-] is stripped, dash runs
 * collapse and leading/trailing dashes are trimmed.
 * @param {string|null|undefined} input
 * @returns {string} normalized slug ('' when nothing usable remains)
 */
export function normalizeSlug(input) {
    if (input == null) return '';
    return String(input)
        .trim()
        .toLowerCase()
        .replace(/[\s_]+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

/**
 * A slug is reserved when it collides with a registered app route (e.g.
 * "billing", "tasks", "list-view"). Such an alias would be permanently shadowed
 * by the real route during navigation, so it is blocked at creation time.
 * @param {string} slug - already-normalized slug
 * @returns {boolean}
 */
export function isReservedSlug(slug) {
    if (!slug) return false;
    return router.isRegisteredPath(`/${slug}`);
}

/**
 * Validate a user-entered alias. The alias is OPTIONAL: empty input is valid and
 * resolves to null (no alias). Uniqueness is enforced by the DB and surfaced at
 * save time, not here.
 * @param {string|null|undefined} rawInput
 * @returns {{ valid: boolean, slug: string|null, error: string|null }}
 */
export function validateSlug(rawInput) {
    const slug = normalizeSlug(rawInput);
    if (!slug) return { valid: true, slug: null, error: null };
    if (isReservedSlug(slug)) {
        return { valid: false, slug, error: `"/${slug}" is a reserved address - pick a different alias.` };
    }
    return { valid: true, slug, error: null };
}

/**
 * Build the in-app hash path (without the leading '#') for a task list. Prefers
 * the friendly slug alias when present, falling back to the canonical id form.
 * @param {{ id?: string, slug?: string|null }} list
 * @returns {string} e.g. "/afs" or "/list-view?id=<uuid>"
 */
export function taskListPath(list) {
    if (list?.slug) return `/${list.slug}`;
    return `/list-view?id=${encodeURIComponent(list?.id ?? '')}`;
}
